import 'react-quill/dist/quill.bubble.css';

import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable
} from 'react-beautiful-dnd';

import { Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Unstable_Grid2';
import { IconButtonWithConfirmation } from '../../../../shared/components/IconButtonWithConfirmation';
import { InlineQuillEdit } from '../../../components/InlineQuillEdit';
import { LessonComponentStyleType } from '../../../models/LessonComponentStyleType';
import { ListComponent } from '../../../models/LessonComponent';
import { ListNumber } from './ListNumber';
import React from 'react';
import { SelectionOption } from '../../../models/LessonComponentContent';
import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';
import styles from '../../../styles/List.module.scss';
import { useLessonEditContext } from '../../../models/LessonEditContext';
import useThemes from '../../../../shared/hooks/useThemes';

interface ListComponentProps {
  styleType: LessonComponentStyleType;
  component: ListComponent;
  placeholder?: string;
}

export const ListComponentEditor = (props: ListComponentProps) => {
  const items: SelectionOption[] = props.component.content?.options || [];
  const { handleSettingsChange } = useLessonEditContext();
  const { muiTheme } = useThemes();
  const fontFamily = muiTheme.typography.fontFamily;

  /** Handle default item change */
  const handleDefaultItemChange = (label: string) => {
    if (label) {
      const newItem: SelectionOption = {
        label: label,
        value: nanoid()
      };
      const copyComp = cloneDeep(props.component);
      copyComp.content.options.push(newItem);
      handleSettingsChange(copyComp);
    }
  };

  /** Handle new item addition by ADD ITEM button */
  const handleAddNewItem = () => {
    const copyComp = cloneDeep(props.component);
    copyComp.content.options.push({ label: '', value: nanoid() });
    handleSettingsChange(copyComp);
  };

  /** Handle existing item change */
  const handleChange = (label: string, value: string) => {
    const copyComp = cloneDeep(props.component);
    const index = copyComp.content.options.findIndex(
      (option) => option.value === value
    );
    copyComp.content.options[index] = {
      label,
      value
    };
    handleSettingsChange(copyComp);
  };

  const reorderOptions = (fromIndex: number, toIndex: number) => {
    const copyComp = cloneDeep(props.component);
    const copyItems: SelectionOption[] = cloneDeep(items);
    const movingItem = items[fromIndex];
    copyItems.splice(fromIndex, 1);
    copyItems.splice(toIndex, 0, movingItem);
    copyComp.content.options = copyItems;
    handleSettingsChange(copyComp);
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    reorderOptions(result.source.index, result.destination.index);
  };

  const getDroppableStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#eee' : 'transparent',
    height: '100%'
  });

  const handleDeleteItem = (id: string) => {
    const copy = cloneDeep(props.component);
    const optionIndex = copy.content.options.findIndex((o) => o.value === id);
    copy.content.options.splice(optionIndex, 1);
    handleSettingsChange(copy);
  };

  return (
    <Grid
      container
      direction="column"
      className={`style-${props.styleType}`}
      style={{ fontFamily }}
    >
      {!items.length ? (
        <Grid container alignItems="center" className={styles.itemSpacing}>
          <ListNumber
            component={props.component}
            sequenceNumber={props.component.settings.listSettings?.startNumber}
          />
          <Grid style={{ width: '100%' }} className={styles.itemLabel}>
            <InlineQuillEdit
              placeholder={props.placeholder}
              value={''}
              componentKey={props.component.componentkey}
              styleType={props.styleType}
              onChange={handleDefaultItemChange}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="column">
          <Grid>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selection-options">
                {(provided1, snapshot1) => (
                  <Grid
                    container
                    direction="column"
                    ref={provided1.innerRef}
                    style={getDroppableStyle(snapshot1.isDraggingOver)}
                  >
                    {items?.map((item, index) => (
                      <Draggable
                        key={item.value}
                        draggableId={item.value}
                        index={index}
                      >
                        {(provided2) => (
                          <Grid
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                          >
                            <Grid
                              container
                              alignItems="center"
                              className={styles.itemSpacing}
                            >
                              <ListNumber
                                component={props.component}
                                sequenceNumber={
                                  props.component.settings.listSettings
                                    ?.startNumber + index
                                }
                              />
                              <Grid className={`style-p ${styles.itemLabel}`}>
                                <InlineQuillEdit
                                  key={item.value}
                                  placeholder={'Click to edit item label'}
                                  value={item.label}
                                  componentKey={props.component.componentkey}
                                  notAllowEmptyValue
                                  styleType={props.styleType}
                                  onChange={(e) => handleChange(e, item.value)}
                                />
                              </Grid>
                              <Grid
                                {...provided2?.dragHandleProps}
                                className={styles.dragIndicatorIcon}
                              >
                                {/* To make it look the same as the 'delete' icon button */}
                                <DragIndicatorIcon htmlColor="rgba(0,0,0,0.54)" />
                              </Grid>
                              <Grid>
                                <IconButtonWithConfirmation
                                  size="small"
                                  onConfirm={() => handleDeleteItem(item.value)}
                                >
                                  <DeleteIcon />
                                </IconButtonWithConfirmation>
                              </Grid>
                            </Grid>
                          </Grid>
                        )}
                      </Draggable>
                    ))}

                    {provided1.placeholder}
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
        </Grid>
      )}
      <Grid className={styles.itemSpacing}>
        <Button
          variant="contained"
          disabled={items.some((i) => !i.label)}
          color="primary"
          size="small"
          onClick={handleAddNewItem}
        >
          Add item
        </Button>
      </Grid>
    </Grid>
  );
};
